import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaHistory, FaComments, FaChevronDown, FaChevronUp, FaPlay } from 'react-icons/fa';
import { getRequest } from '../../services/api/apiService';
import { useLang } from '../../i18n/LanguageContext';

// ── Page « Chat History » ─────────────────────────────────────────
// Toutes les conversations passées avec Alex, triées par ordre
// chronologique (plus récente d'abord). Chaque conversation se déplie pour
// montrer le transcript COMPLET (allers-retours client ↔ Alex, sauvegardés
// tour par tour en BD — chat_messages), et « Continuer la conversation »
// rouvre la session dans le chat Alex (/dashboard/alex?session=ID) : la
// discussion reprend exactement où elle s'était arrêtée.
const ChatHistoryPage = () => {
  const navigate = useNavigate();
  const { t } = useLang();
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openId, setOpenId] = useState(null);
  const [transcripts, setTranscripts] = useState({}); // sessionId → messages[]
  const [transcriptLoading, setTranscriptLoading] = useState(false);

  useEffect(() => {
    getRequest('/api/chat/sessions')
      .then((res) => setSessions(res.sessions || []))
      .catch(() => setSessions([]))
      .finally(() => setLoading(false));
  }, []);

  const toggle = async (session) => {
    const id = session.sessionId;
    if (openId === id) return setOpenId(null);
    setOpenId(id);
    if (!transcripts[id]) {
      setTranscriptLoading(true);
      try {
        const res = await getRequest(`/api/chat/sessions/${id}/messages`);
        setTranscripts((prev) => ({ ...prev, [id]: res.messages || [] }));
      } catch {
        setTranscripts((prev) => ({ ...prev, [id]: [] }));
      } finally {
        setTranscriptLoading(false);
      }
    }
  };

  const fmtDate = (d) => {
    const date = d ? new Date(d) : null;
    if (!date || isNaN(date.getTime())) return '';
    return date.toLocaleString('fr-FR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="p-3 sm:p-6 max-w-3xl mx-auto">
      {/* En-tête */}
      <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-[#221C51] flex items-center justify-center text-white text-lg flex-shrink-0">
            <FaHistory />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-[#221C51]">{t('Chat History')}</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {t('Toutes vos conversations avec Alex — cliquez pour relire, continuez où vous vous étiez arrêté.')}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate('/dashboard/alex')}
          className="px-4 py-2.5 text-sm font-semibold text-white bg-[#221C51] rounded-xl hover:bg-[#2d2570] transition"
        >
          {t('➕ Nouvelle conversation')}
        </button>
      </div>

      {loading ? (
        <div className="flex flex-col items-center justify-center py-24">
          <div className="w-8 h-8 border-4 border-[#221C51]/20 border-t-[#221C51] rounded-full animate-spin" />
          <p className="text-sm text-gray-400 mt-4">{t("Chargement de l'historique…")}</p>
        </div>
      ) : sessions.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="w-16 h-16 rounded-full bg-gray-100 flex items-center justify-center text-3xl mb-4">💬</div>
          <p className="text-sm font-semibold text-[#221C51]">{t('Aucune conversation pour l\'instant')}</p>
          <p className="text-xs text-gray-400 mt-1 mb-5">{t('Chaque échange avec Alex est sauvegardé automatiquement ici.')}</p>
          <button
            onClick={() => navigate('/dashboard/alex')}
            className="px-5 py-2.5 text-sm font-semibold text-white bg-[#221C51] rounded-xl hover:bg-[#2d2570] transition"
          >
            {t('Démarrer avec Alex →')}
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {sessions.map((s) => {
            const open = openId === s.sessionId;
            const msgs = transcripts[s.sessionId];
            return (
              <div key={s.sessionId} className={`bg-white border rounded-2xl overflow-hidden transition ${open ? 'border-[#221C51]/40 shadow-md' : 'border-gray-200 hover:border-[#221C51]/30'}`}>
                {/* Ligne de résumé */}
                <button onClick={() => toggle(s)} className="w-full text-left px-4 py-3.5 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-[#221C51]/10 flex items-center justify-center text-[#221C51] flex-shrink-0">
                    <FaComments className="text-sm" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-[#221C51] truncate">{s.preview || 'Conversation'}</p>
                    <p className="text-[11px] text-gray-400 mt-0.5">
                      {fmtDate(s.lastAt)} · {s.messageCount} message{s.messageCount > 1 ? 's' : ''}
                    </p>
                  </div>
                  <span className="text-gray-300 flex-shrink-0">{open ? <FaChevronUp /> : <FaChevronDown />}</span>
                </button>

                {/* Transcript complet (allers-retours) */}
                {open && (
                  <div className="border-t border-gray-100">
                    <div className="max-h-96 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
                      {transcriptLoading && !msgs ? (
                        <p className="text-xs text-gray-400 text-center py-6">Chargement du transcript…</p>
                      ) : (msgs || []).length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-6">Transcript indisponible.</p>
                      ) : (
                        msgs.map((m, i) => (
                          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-[85%] px-3.5 py-2.5 rounded-2xl text-xs leading-relaxed whitespace-pre-wrap break-words ${
                              m.role === 'user'
                                ? 'bg-[#221C51] text-white rounded-br-md'
                                : 'bg-white border border-gray-200 text-gray-700 rounded-bl-md'
                            }`}>
                              {m.role !== 'user' && <span className="block text-[10px] font-bold text-[#221C51] mb-1">👔 Alex</span>}
                              {m.content}
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                    <div className="px-4 py-3 bg-white border-t border-gray-100 flex justify-end">
                      <button
                        onClick={() => navigate(`/dashboard/alex?session=${s.sessionId}`)}
                        className="inline-flex items-center gap-2 px-4 py-2 text-xs font-semibold text-white bg-[#221C51] rounded-xl hover:bg-[#2d2570] transition"
                      >
                        <FaPlay className="text-[10px]" /> {t('Continuer la conversation')}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ChatHistoryPage;
